package dev.lingvo.site.pages.app.projects.slug.screenshots

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import dev.lingvo.shared.DesignVersionSummaryDto
import dev.lingvo.shared.Role
import dev.lingvo.site.state.ProjectAuthority
import dev.lingvo.shared.DeviceClass
import dev.lingvo.shared.RestoreDesignRequest
import dev.lingvo.shared.ScreenshotDesignDto
import dev.lingvo.shared.screenshots.doc.DecodeResult
import dev.lingvo.shared.screenshots.doc.DesignCodec
import dev.lingvo.shared.screenshots.doc.ScreenshotDesign
import dev.lingvo.shared.screenshots.doc.StoreDeviceClassRule
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.ApiException
import dev.lingvo.site.api.Session
import dev.lingvo.site.api.restoreScreenshotDesign
import dev.lingvo.site.api.screenshotDesignVersion
import dev.lingvo.site.api.screenshotDesignVersions
import dev.lingvo.site.components.studio.preview.DesignLooksPort
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LConfirmDialog
import dev.lingvo.site.ds.LConfirmTone
import dev.lingvo.site.ds.LModal
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LoadingState
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr
import kotlin.js.Date
import dev.lingvo.site.i18n.trf
import dev.lingvo.site.state.authoritySubject
import dev.lingvo.site.state.liveProjectRoute
import dev.lingvo.site.state.requestProjectResource
import kotlinx.browser.document
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.yield
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement

/** The safe first action loads the fetched version; keeping this draft explicitly rebases a new version. */
@Composable
fun DesignConflict(controller: DesignAutosaveController) {
    val open = controller.state == DesignSaveState.CONFLICT
    val savedAt = controller.conflict?.savedAt?.let(Date::parse)
    val minutes = savedAt?.takeIf { it.isFinite() }?.let { ((Date.now() - it) / 60_000).toInt().coerceAtLeast(0) }
    val title = when (minutes) {
        null -> tr("app.projects.slug.screenshots.design_conflict.someone_else_saved_this_design", "Someone else saved this design")
        0 -> tr("app.projects.slug.screenshots.design_conflict.someone_else_saved_this_design_just", "Someone else saved this design just now.")
        1 -> tr("app.projects.slug.screenshots.design_conflict.someone_else_saved_this_design_1", "Someone else saved this design 1 minute ago.")
        else -> trf("screenshots.design_conflict.minutes", "Someone else saved this design {minutes} minutes ago.", "minutes" to minutes)
    }
    LModal(
        open = open,
        title = title,
        onClose = {},
        actions = {
            // LModal mounts after its presence effect runs. Focus from the mounted actions so a
            // closed-to-open transition cannot consume the handoff before the button exists.
            LaunchedEffect(open, controller.conflict) {
                if (open && controller.conflict != null) {
                    yield()
                    (document.querySelector("[data-design-conflict-default]") as? HTMLElement)?.focus()
                }
            }
            LButton(tr("app.projects.slug.screenshots.design_conflict.load_their_version", "Load their version"), controller::loadTheirs,
                modifier = Modifier.attr("data-design-conflict-default", "true"), enabled = controller.conflict != null)
            LButton(tr("app.projects.slug.screenshots.design_conflict.keep_mine", "Keep mine"), controller::keepMine, variant = LButtonVariant.SECONDARY,
                enabled = controller.conflict != null)
            if (controller.conflict == null) LButton(tr("app.projects.slug.screenshots.design_conflict.retry", "Retry"), controller::retry, variant = LButtonVariant.GHOST)
        },
    ) {
        P { Text(tr("app.projects.slug.screenshots.design_conflict.loading_their_version_is_the_safest", "Loading their version is the safest choice. Your unsaved design stays available as a local draft.")) }
        P { Text(tr("app.projects.slug.screenshots.design_conflict.keep_mine_saves_your_design_as", "Keep mine saves your design as a new version. Their saved version remains in the 50-version history.")) }
        controller.issues.forEach { issue -> P { Text(issue) } }
    }
}


/** History previews remain readable to viewers; restoring appends a version through the guarded writer. */
@Composable
internal fun DesignVersions(
    data: DesignPageData, controller: DesignAutosaveController, runner: ScreenshotsActionRunner,
    looks: DesignLooksPort, device: DeviceClass,
    locale: String, canWrite: Boolean, onRestoring: (Boolean) -> Unit,
) {
    val subject = liveProjectRoute(data.project.slug)?.authoritySubject(Session.organizations)
    var open by remember(subject) { mutableStateOf(false) }
    var versions by remember(subject) { mutableStateOf<List<DesignVersionSummaryDto>>(emptyList()) }
    var selected by remember(subject) { mutableStateOf<ScreenshotDesignDto?>(null) }
    var requested by remember(subject) { mutableStateOf<Int?>(null) }
    var pending by remember(subject) { mutableStateOf<ScreenshotDesignDto?>(null) }
    var loading by remember(subject) { mutableStateOf(false) }
    var busy by remember(subject) { mutableStateOf(false) }
    var failure by remember(subject) { mutableStateOf<String?>(null) }
    var reload by remember(subject) { mutableStateOf(0) }
    val canManage = canWrite && ProjectAuthority.resolve(liveProjectRoute(data.project.slug)?.project, Session.organizations).role == Role.OWNER
    LaunchedEffect(open, subject, reload) {
        if (open && subject != null) {
            loading = true
            failure = null
            try {
                versions = requestProjectResource(subject.action, data.project.slug) {
                    ApiClient.screenshotDesignVersions(data.project.slug, data.plan.platform)
                }
            } catch (cancelled: CancellationException) { throw cancelled }
            catch (_: Throwable) { failure = tr("app.projects.slug.screenshots.design_conflict.couldnt_load_the_design_history_retry", "Couldn't load the design history. Retry.") }
            finally { loading = false }
        }
    }
    LaunchedEffect(open, subject, requested) {
        val version = requested
        if (open && subject != null && version != null) {
            loading = true
            failure = null
            try {
                selected = requestProjectResource(subject.action, data.project.slug) {
                    ApiClient.screenshotDesignVersion(data.project.slug, data.plan.platform, version)
                }
            } catch (cancelled: CancellationException) { throw cancelled }
            catch (_: Throwable) { failure = tr("app.projects.slug.screenshots.design_conflict.couldnt_load_this_version_choose_another", "Couldn't load this version. Choose another version or retry.") }
            finally { loading = false }
        }
    }
    LButton(tr("app.projects.slug.screenshots.design_conflict.versions", "Versions"), onClick = { open = true }, variant = LButtonVariant.SECONDARY)
    LModal(open, tr("app.projects.slug.screenshots.design_conflict.design_versions", "Design versions"), onClose = { open = false; requested = null; selected = null }) {
        LButton(tr("app.projects.slug.screenshots.design_conflict.reload_history", "Reload history"), onClick = { selected = null; reload++ }, enabled = !loading, variant = LButtonVariant.SECONDARY)
        if (loading) LoadingState(rows = 3)
        failure?.let { message ->
            LAlert(title = message, variant = LAlertVariant.WARNING)
            LButton(tr("app.projects.slug.screenshots.design_conflict.retry", "Retry"), onClick = { selected = null; requested = null; reload++ }, variant = LButtonVariant.SECONDARY)
        }
        if (canManage) {
            val version = requested ?: controller.version
            DesignSnapshotEditor(data, version, versions.firstOrNull { it.version == version }?.snapshot, runner,
                controller.state == DesignSaveState.CLEAN && !busy, onChanged = { deleted -> selected = null; if (deleted) requested = null; reload++ })
        }
        versions.forEach { version ->
            Div(attrs = { style { property("overflow-wrap", "anywhere"); property("white-space", "pre-wrap") } }) {
                version.snapshot?.let { pin ->
                    P { Text(pin.name) }
                    pin.note?.let { P { Text(it) } }
                    LMuted(trf("screenshots.snapshots.named_by", "Named by {actor} · {time}", "actor" to pin.namedBy, "time" to pin.namedAt))
                    if (pin.revision > 1) LMuted(trf("screenshots.snapshots.updated_by", "Updated by {actor} · {time}", "actor" to pin.updatedBy, "time" to pin.updatedAt))
                }
                LMuted(trf("screenshots.snapshots.saved_by", "Saved by {actor} · {time}", "actor" to version.savedBy, "time" to version.savedAt))
                LButton(trf("screenshots.design_versions.version", "Version {version} · {time}", "version" to version.version, "time" to version.savedAt),
                    onClick = { requested = version.version; selected = null }, variant = LButtonVariant.SECONDARY,
                    modifier = Modifier.styleModifier { property("max-width", "100%"); property("white-space", "normal"); property("overflow-wrap", "anywhere"); property("min-height", "40px") })
            }
        }
        selected?.let { saved ->
            val decoded = DesignCodec.decodeElement(saved.document, StoreDeviceClassRule)
            if (decoded is DecodeResult.Ok) {
                LAlert(title = tr("app.projects.slug.screenshots.design_conflict.this_historical_preview_is_read_only", "This historical preview is read-only."))
                ShotPreviewGrid(data, decoded.design, device, locale, looks, onFits = {}, allLocales = false)
                if (canWrite && (versions.firstOrNull { it.version == saved.version }?.snapshot == null || canManage) && controller.state == DesignSaveState.CLEAN && saved.version != controller.version) {
                    LButton(tr("app.projects.slug.screenshots.design_conflict.restore_this_version", "Restore this version"), onClick = { pending = saved; open = false }, variant = LButtonVariant.SECONDARY)
                } else if (canWrite && controller.state != DesignSaveState.CLEAN) LMuted(
                    tr("app.projects.slug.screenshots.design_conflict.save_or_resolve_your_current_changes", "Save or resolve your current changes before restoring a version."))
            } else LAlert(title = tr("app.projects.slug.screenshots.design_conflict.this_version_cannot_be_previewed_safely", "This version cannot be previewed safely."))
        }
    }
    LConfirmDialog(
        open = pending != null, title = tr("app.projects.slug.screenshots.design_conflict.restore_this_design_version", "Restore this design version?"),
        confirmLabel = tr("app.projects.slug.screenshots.design_conflict.restore_version", "Restore version"), tone = LConfirmTone.NEUTRAL,
        consequenceTitle = tr("app.projects.slug.screenshots.design_conflict.restore_keeps_saved_history", "Saved versions stay in history"),
        consequence = tr("app.projects.slug.screenshots.design_conflict.restoring_appends_a_new_saved_version", "Restoring appends a new saved version. Your current saved version stays in the 50-version history."),
        busy = busy, error = if (pending != null) failure else null,
        onCancel = { if (!busy) { pending = null; failure = null } }, onConfirm = {
            val target = pending
            if (target != null && canWrite && controller.state == DesignSaveState.CLEAN && !busy) {
                val base = controller.version
                val original = controller.document
                runner.run(if (versions.firstOrNull { it.version == target.version }?.snapshot != null) ScreenshotsPrivilege.SNAPSHOT else ScreenshotsPrivilege.DESIGN, { busy = it; onRestoring(it) },
                    { if (versions.firstOrNull { it.version == target.version }?.snapshot != null)
                        DesignSnapshotApi.restore(data.project.slug, data.plan.platform, target.version, base)
                      else ApiClient.restoreScreenshotDesign(data.project.slug, data.plan.platform, target.version, RestoreDesignRequest(base)) },
                    { error ->
                        val api = error as? ApiException
                        if (api?.status == 409 && api.code == "design_version_conflict") {
                            pending = null
                            failure = null
                            controller.reconcileConflict(api)
                        } else failure = api?.message ?: tr("app.projects.slug.screenshots.design_conflict.couldnt_restore_this_version_retry", "Couldn't restore this version. Retry.")
                    },
                    { saved ->
                        if (controller.adoptRestored(saved, base, original)) { pending = null; selected = null; requested = null; failure = null }
                        else failure = tr("app.projects.slug.screenshots.design_conflict.the_version_was_restored_your_newer", "The version was restored. Your newer local edits are still here; save them to resolve against the latest version.")
                    })
            }
        },
    )
}
